/**
 * @file The `saveAnalyticsConfig` callable contract, the only writer of `parameters/analytics` and
 * `parameters/analyticsBindings`. Sent by Settings → Analytics, served from `functions/main`.
 * Every request names its action: `save` sets and removes top-level keys on one document of
 * `ANALYTICS_CONFIG_DOCUMENT_PATHS`, `turnOff` deletes every one of them and carries no save field.
 * A turn-off is never inferred from absent fields, so an empty payload is refused and deletes
 * nothing. The type states the shape only; the callable checks the payload at run time and refuses
 * `invalid-argument` naming the field.
 */
import { ANALYTICS_BINDINGS_PATH } from './Analytics.Bindings.types';
import { ANALYTICS_PARAMETERS_PATH } from './Analytics.Parameters.types';

export const SAVE_ANALYTICS_CONFIG_ACTIONS = ['save', 'turnOff'] as const;

export type SaveAnalyticsConfigAction = (typeof SAVE_ANALYTICS_CONFIG_ACTIONS)[number];

/** The document names a save may carry, each mapped to the Firestore path the callable writes. */
export const ANALYTICS_CONFIG_DOCUMENT_PATHS = {
  analytics: ANALYTICS_PARAMETERS_PATH,
  analyticsBindings: ANALYTICS_BINDINGS_PATH,
} as const;

export type AnalyticsConfigDocument = keyof typeof ANALYTICS_CONFIG_DOCUMENT_PATHS;

export type SaveAnalyticsConfigRequest =
  | {
      action: 'save';
      document: AnalyticsConfigDocument;
      /** The top-level keys to set, with their values, over the stored document. */
      set: Record<string, unknown>;
      /** The top-level keys to remove; none may also appear in `set`. */
      remove: string[];
    }
  | { action: 'turnOff' };

export type SaveAnalyticsConfigResponse = { action: SaveAnalyticsConfigAction };
